import React, { useState, useEffect, useRef } from 'react';
import './room1.css';
import './room2.css';
import './pipes.css';
import Settings from '../components/common/settings';
import Lost from '../components/common/lost';
import { useNavigate } from 'react-router-dom';

import settingsSound from '../audios/settoings.wav';

const TOTAL_TIME = 10 * 60;
const ROOM_KEY = 'room2_time'; 

const BASE_SHAPES = {
  HORIZ:     [false, true,  false, true ],  
  VERT:      [true,  false, true,  false],
  CORNER_RD: [false, true,  true,  false], 
  CORNER_LD: [true,  false, false, true ], 
  CORNER_LU: [false, false, true,  true ], 
  DEAD:      [false, true,  false, false],  
};

function rotateSides(sides, steps) {
  let r = [...sides];
  const s = ((steps % 4) + 4) % 4;
  for (let i = 0; i < s; i++) r = [r[3], r[0], r[1], r[2]];
  return r;
}
const SOLUTION = [
  { shape: 'HORIZ',     rot: 0, fixed: true  }, 
  { shape: 'HORIZ',     rot: 0, fixed: false },
  { shape: 'HORIZ',     rot: 0, fixed: false }, 
  { shape: 'CORNER_LU', rot: 0, fixed: false },
  { shape: 'DEAD',      rot: 0, fixed: true  },
  { shape: 'DEAD',      rot: 0, fixed: true  }, 
  { shape: 'DEAD',      rot: 0, fixed: true  },
  { shape: 'VERT',      rot: 0, fixed: false }, 
  { shape: 'CORNER_LU', rot: 2, fixed: true  }, 
  { shape: 'HORIZ',     rot: 0, fixed: false }, 
  { shape: 'HORIZ',     rot: 0, fixed: false },
  { shape: 'CORNER_LD', rot: 0, fixed: false },
];
// Scramble: give non-fixed tiles a clearly wrong rotation
const SCRAMBLE = { 1: 1, 2: 2, 3: 1, 7: 3, 9: 3, 10: 1, 11: 2 };

function buildStart() {
  return SOLUTION.map((tile, i) =>
    SCRAMBLE[i] !== undefined ? SCRAMBLE[i] : tile.rot
  );
}

function checkSolved(rotations) {
  return SOLUTION.every((tile, i) => {
    if (tile.shape === 'DEAD') return true;
    return ((rotations[i] % 4) + 4) % 4 === tile.rot;
  });
}
const COLS = 4;
const ROWS = 3;

function getNeighbour(idx, dir) {
  const col = idx % COLS;
  const row = Math.floor(idx / COLS);
  if (dir === 0) return row > 0        ? idx - COLS : -1;
  if (dir === 1) return col < COLS - 1 ? idx + 1    : -1;
  if (dir === 2) return row < ROWS - 1 ? idx + COLS : -1;
  if (dir === 3) return col > 0        ? idx - 1    : -1;
  return -1;
}

const OPPOSITE = { 0: 2, 1: 3, 2: 0, 3: 1 };

function computeConnected(rotations) {
  const connected = new Set([0]);
  const queue = [0];
  while (queue.length) {
    const cur = queue.shift();
    const sides = rotateSides(BASE_SHAPES[SOLUTION[cur].shape], rotations[cur]);
    for (let dir = 0; dir < 4; dir++) {
      if (!sides[dir]) continue;
      const nb = getNeighbour(cur, dir);
      if (nb < 0 || connected.has(nb)) continue;
      const nbSides = rotateSides(BASE_SHAPES[SOLUTION[nb].shape], rotations[nb]);
      if (nbSides[OPPOSITE[dir]]) {
        connected.add(nb);
        queue.push(nb);
      }
    }
  }
  return connected;
}
const PATH   = new Set([0, 1, 2, 3, 7, 8, 9, 10, 11]);
const DECOYS = new Set([4, 5, 6]);

const PipeTile = ({ index, shape, rotation, onClick, fixed, solved, connected }) => {
  const sides = rotateSides(BASE_SHAPES[shape], rotation);
  const [top, right, bottom, left] = sides;
  const isDecoy = DECOYS.has(index);
  const isPath  = PATH.has(index);
  const isEntry = index === 0;
  const isExit  = index === 8;
  const isLit   = solved && isPath;
  const isWarm  = !solved && connected && isPath;

  return (
    <div
      className={[
        'pipe-tile',
        fixed   ? 'pipe-fixed'  : '',
        isDecoy ? 'pipe-decoy'  : '',
        isLit   ? 'pipe-lit'    : '',
        isWarm  ? 'pipe-warm'   : '',
        isEntry ? 'pipe-entry'  : '',
        isExit  ? 'pipe-exit'   : '',
      ].join(' ')}
      onClick={fixed ? null : onClick}
      style={{ cursor: fixed ? 'default' : 'pointer' }}
    >
      <div className="pipe-circle">
        <div className="pipe-bolt pipe-bolt-tl" />
        <div className="pipe-bolt pipe-bolt-tr" />
        <div className="pipe-bolt pipe-bolt-bl" />
        <div className="pipe-bolt pipe-bolt-br" />
        {top    && <div className="pipe-seg pipe-top"    />}
        {right  && <div className="pipe-seg pipe-right"  />}
        {bottom && <div className="pipe-seg pipe-bottom" />}
        {left   && <div className="pipe-seg pipe-left"   />}
        <div className="pipe-center" />
        {(isEntry || isExit) && (
          <div className="pipe-badge">{isEntry ? 'IN' : 'OUT'}</div>
        )}
      </div>
    </div>
  );
};

const Pipes = () => {
  const navigate = useNavigate();

  const [timeLeft,      setTimeLeft]      = useState(TOTAL_TIME);
  const [openSettings,  setOpenSettings]  = useState(false);
  const [showLosePopup, setShowLosePopup] = useState(false);
  const [rotations,     setRotations]     = useState(buildStart);
  const [solved,        setSolved]        = useState(false);
  const [solvedAnim,    setSolvedAnim]    = useState(false);
  const [connected,     setConnected]     = useState(() => computeConnected(buildStart()));

  const inventory     = ['magnifying_glass'];
  const settingsAudio = useRef(null);

  useEffect(() => {
    settingsAudio.current = new Audio(settingsSound);
  }, []);

  // reads the same room2_time key that Room2.jsx set — never resets it here,
  // so the countdown keeps running across the room's pages instead of restarting
  useEffect(() => {
    const interval = setInterval(() => {
      const saved = localStorage.getItem(ROOM_KEY);
      if (!saved) return;
      const elapsed   = Math.floor((Date.now() - Number(saved)) / 1000);
      const remaining = Math.max(TOTAL_TIME - elapsed, 0);
      setTimeLeft(remaining);
      if (remaining === 0) { setShowLosePopup(true); clearInterval(interval); }
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const rotateTile = (index) => {
    if (solved) return;
    const next = rotations.map((r, i) => i === index ? r + 1 : r);
    setRotations(next);
    setConnected(computeConnected(next));
    if (checkSolved(next)) {
      setSolvedAnim(true);
      setTimeout(() => setSolved(true), 800);
    }
  };

  const playSettingsSound = () => {
    if (!settingsAudio.current) return;
    settingsAudio.current.currentTime = 0;
    settingsAudio.current.play().catch(() => {});
  };

  const minutes     = String(Math.floor(timeLeft / 60)).padStart(2, '0');
  const seconds     = String(timeLeft % 60).padStart(2, '0');
  const isWarning   = timeLeft <= 60;
  const fillPercent = (timeLeft / TOTAL_TIME) * 100;

  return (
    <section className="pipes">
      <div className="inventorybar">
        {inventory.includes('magnifying_glass') && (
          <div className="inventory-item magnify-icon" />
        )}
      </div>
      <div className="room1nav">
        <div />
        <div className="roomtimer">
          <span className="timerlabel">TIME REMAINING</span>
          <div className="timerdiv">
            <span className={`timerdigits ${isWarning ? 'timerwarning' : ''}`}>
              {minutes}:{seconds}
            </span>
          </div>
          <div className="timerbar">
            <div
              className="timerbartill"
              style={{ width: `${fillPercent}%`, background: isWarning ? '#ff4444' : '#D4AF37' }}
            />
          </div>
        </div>
        <div className="settings" onClick={() => { playSettingsSound(); setOpenSettings(true); }} />
      </div>

      <div className={`pipe-panel ${solvedAnim ? 'pipe-panel-solved' : ''}`}>
        <p className="pipe-hint">Rotate the pipes to connect IN → OUT</p>
        <div className="pipe-grid">
          {SOLUTION.map((tile, i) => (
            <PipeTile
              key={i}
              index={i}
              shape={tile.shape}
              rotation={rotations[i]}
              fixed={tile.fixed}
              solved={solvedAnim}
              connected={connected.has(i)}
              onClick={() => rotateTile(i)}
            />
          ))}
        </div>

      </div>

     
        <button
            className="nextbt"
            onClick={() => navigate('/room2task3')}
            >
          NEXT
        </button>
      <Settings
        open={openSettings}
        onClose={() => { playSettingsSound(); setOpenSettings(false); }}
        onPlaySound={playSettingsSound}
        onNavigate={navigate}
      />

      <Lost
        open={showLosePopup}
        onRetry={() => { localStorage.removeItem(ROOM_KEY); window.location.reload(); }}
      />

    </section>
  );
};

export default Pipes;